import { useCallback } from "react";
import { Alert, Typography } from "@mui/material";
import { useAdminImpersonateUser } from "@queries/users/useAdminImpersonateUser";
import { ImpersonateLinkDialogContent } from "@features/Users/ImpersonateLinkDialogContent";
import { blurActiveElement } from "@shared/utils/blur-active-element";
import { toast } from "@shared/services/toast";
import { useUIStore } from "@store/ui.store";

type Params = {
  email: string;
  name: string | undefined;
};

export const useAdminImpersonateAction = () => {
  const confirmDialog = useUIStore((state) => state.confirmDialog);
  const impersonateUser = useAdminImpersonateUser();

  const impersonate = useCallback(
    async ({ email, name = "Unknown" }: Params) => {
      if (!email) return;

      const answer = await confirmDialog({
        title: "Impersonate user?",
        content: (
          <>
            <Typography>{name}</Typography>
            <Typography>{email}</Typography>
            <Alert severity="warning" icon={false} sx={{ mt: 2 }}>
              You&apos;ll get a one-time link to sign in as this user in a
              separate browser session — your own session here won&apos;t be
              affected.
            </Alert>
          </>
        ),
        confirmButtonText: "Impersonate",
        cancelButtonText: "Cancel",
      });

      blurActiveElement();

      if (!answer) return;

      let link: string;
      try {
        link = await toast.promise(impersonateUser.mutateAsync({ email }), {
          pending: "Generating impersonation link…",
        });
      } catch {
        return;
      }

      await confirmDialog({
        title: "Impersonation link ready",
        content: <ImpersonateLinkDialogContent link={link} />,
        confirmButtonText: "Done",
        cancelButtonText: false,
        maxWidth: "sm",
        fullWidth: true,
      });
    },
    [confirmDialog, impersonateUser],
  );

  return { impersonate, isPending: impersonateUser.isPending };
};
